import "./booking.scss";
import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Polyline,
  Autocomplete as GMapAutocomplete,
} from "@react-google-maps/api";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { RiDragMove2Fill } from "react-icons/ri";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Checkbox as MuiCheckbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  addNewBooking,
  editBooking,
  fetchBookingById,
  fetchCompany,
  fetchCustomer,
  fetchDriver,
  fetchVehicle,
} from "../../store/slices/thunk";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast, ToastContainer } from "react-toastify";
import dayjs, { Dayjs } from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import { getTenantConfigFromStorage } from "../../helpers/tenantConfig";

declare global {
  interface Window {
    google: any;
  }
}

const startTimeWithTodaysDate = (existingStartTimeMs: number): number => {
  if (!existingStartTimeMs) return 0;
  const t = dayjs(existingStartTimeMs);
  return dayjs()
    .hour(t.hour())
    .minute(t.minute())
    .second(0)
    .millisecond(0)
    .valueOf();
};

const containerStyle = { width: "100%", height: "350px" };
const defaultCenter = { lat: 24.8607, lng: 67.0011 };

interface RowProps {
  item: any;
}

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    height: "46px",
    "& fieldset": { borderColor: "#d0d5dd" },
    "&:hover fieldset": { borderColor: "#7c6dff" },
    "&.Mui-focused fieldset": { borderColor: "#7c6dff" },
  },
};

const selectSx = {
  ...textFieldSx,
  "& .MuiOutlinedInput-root": {
    ...textFieldSx["& .MuiOutlinedInput-root"],
    height: "46px",
  },
};

const FormLabel = ({ label, required }: { label: string; required?: boolean }) => (
  <Typography
    variant="body2"
    sx={{
      fontWeight: 600,
      color: "#344054",
      fontSize: "0.85rem",
      mb: 0.75,
    }}
  >
    {label}
    {required && <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>*</Box>}
  </Typography>
);

const renderField = (
  label: string,
  required: boolean,
  field: React.ReactNode
) => (
  <Box>
    <FormLabel label={label} required={required} />
    {field}
  </Box>
);

const AddBooking = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places", "geometry"],
  });
  const mapRef = useRef<any>(null);
  const tenantConfig = getTenantConfigFromStorage();
  const parentLabel = tenantConfig.labels.parent;
  const schoolLabel = tenantConfig.labels.school;
  const parentLabelPlural = parentLabel.endsWith("y")
    ? `${parentLabel.slice(0, -1)}ies`
    : `${parentLabel}s`;
  const canAddChild = tenantConfig.features.canAddChild;
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [map, setMap] = useState<any>(null);
  const [openAutoId, setOpenAutoId] = React.useState<string | null>(null);

  const [startLocation, setStartLocation] = useState<null | {
    lat: number;
    lng: number;
    address: string;
  }>(null);
  const [endLocation, setEndLocation] = useState<null | {
    lat: number;
    lng: number;
    address: string;
  }>(null);
  const [directions, setDirections] = useState<any>(null);
  const [search, setSearch] = React.useState("");
  const [searchDriver, setSearchDriver] = React.useState("");
  const [selectedCompanyLocation, setSelectedCompanyLocation] =
    useState<any>(null);

  const [addBooking, setAddBooking] = useState({
    customer: [] as any[],
    vehicle: { vehicleId: "", vehicleName: "" },
    driver: { driverId: "", driverName: "" },
    tripType: "Single Trip",
    startLocation: { coordinates: [] as number[], type: "Point", streetAddress: "" },
    company: { companyId: "", companyName: "" },
    endLocation: { coordinates: [] as number[], type: "Point", streetAddress: "" },
    approxTotalKm: "",
    tripStartDate: 0,
    tripEndDate: 0,
    totalAmount: 1,
    tripStatus: "yetToStart",
    status: "",
    tripSubType: "homeToOffice",
    startTime: 0,
    tripName: "",
    isEmailSend: false,
    _id: "",
  });
  const [allCustomer, setAllCustomer] = useState<any[]>([]);
  const [allDriver, setAllDriver] = useState<any[]>([]);

  const { id } = useParams<any>();
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(false);
  const dispatch: any = useDispatch();
  const [getAllVehicle, setAllVehicle] = useState<any[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<any[]>([]);
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const DEBOUNCE_DELAY = 500;
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any>({
    startLocation: [],
    endLocation: [],
  });
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [searchCompany, setSearchCompany] = React.useState("");
  const [companyId, setCompanyId] = useState<string>("");
  const [allCompany, setAllCompany] = useState<any>([]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = addBooking.customer.findIndex((item: any) => item.customerId === active.id);
      const newIndex = addBooking.customer.findIndex((item: any) => item.customerId === over.id);
      const newCustomerArray = arrayMove(addBooking.customer, oldIndex, newIndex);
      setAddBooking((prev: any) => ({ ...prev, customer: newCustomerArray }));
    }
  };

  useEffect(() => {
    const getAllCompany = async () => {
      const response: any = await dispatch(fetchCompany(1, 99999999, searchCompany, ""));
      if (response && response?.data?.data?.length > 0 && response?.isSuccess) {
        setAllCompany(response?.data?.data);
      } else {
        setAllCompany([]);
      }
    };
    getAllCompany();
  }, [dispatch]);

  const handleChangeForCompany = (e: any) => {
    const { value } = e.target;
    setCompanyId(value);
    if (!value) {
      setAddBooking({ ...addBooking, company: { companyId: "", companyName: "" } });
      return;
    }
    const selectedCompany: any = allCompany.find((company: any) => company._id === value);
    if (selectedCompany) {
      if (addBooking.tripSubType === "homeToOffice") {
        setSelectedCompanyLocation(selectedCompany?.schoolLocation);
        setAddBooking((prev: any) => ({ ...prev, endLocation: selectedCompany?.schoolLocation }));
        setEndLocation({
          address: selectedCompany?.schoolLocation?.streetAddress,
          lat: selectedCompany?.schoolLocation?.coordinates[1],
          lng: selectedCompany?.schoolLocation?.coordinates[0],
        });
      }
      if (addBooking.tripSubType === "officeToHome") {
        const selectedDriver: any = allDriver.find((driver: any) => driver._id === value);
        setAddBooking((prev: any) => ({
          ...prev,
          endLocation: selectedDriver?.address,
          startLocation: selectedCompany?.schoolLocation,
        }));
        setEndLocation({
          address: selectedDriver?.address?.streetAddress,
          lat: selectedDriver?.address?.coordinates[1],
          lng: selectedDriver?.address?.coordinates[0],
        });
        setStartLocation({
          address: selectedCompany?.schoolLocation?.streetAddress,
          lat: selectedCompany?.schoolLocation?.coordinates[1],
          lng: selectedCompany?.schoolLocation?.coordinates[0],
        });
      }
      setAddBooking({ ...addBooking, company: { companyId: selectedCompany?._id, companyName: selectedCompany?.companyName } });
      updateTripName(selectedCompany, addBooking.tripSubType, addBooking.driver);
    }
  };

  useEffect(() => {
    const updatedCustomerArray = selectedCustomers.map((cust: any) => ({
      customerId: cust._id,
      customerName: cust.name,
      pickupTime: cust.pickupTime || 0,
    }));
    setAddBooking((prev: any) => ({ ...prev, customer: updatedCustomerArray }));
  }, [selectedCustomers]);

  const handleChangeForDriver = (e: any) => {
    const { value } = e.target;
    if (!value) {
      setAddBooking({ ...addBooking, driver: { driverId: "", driverName: "" } });
      return;
    }
    const selectedDriver: any = allDriver.find((driver: any) => driver._id === value);
    if (selectedDriver) {
      if (addBooking.tripSubType === "homeToOffice") {
        setStartLocation({
          address: selectedDriver?.address?.streetAddress,
          lat: selectedDriver?.address?.coordinates[1],
          lng: selectedDriver?.address?.coordinates[0],
        });
        setAddBooking({
          ...addBooking,
          startLocation: {
            streetAddress: selectedDriver?.address?.streetAddress,
            coordinates: selectedDriver?.address?.coordinates,
            type: selectedDriver?.address?.type,
          },
          driver: { driverId: selectedDriver?._id, driverName: selectedDriver?.driverName },
        });
      }
      if (addBooking.tripSubType === "officeToHome") {
        setEndLocation({
          address: selectedDriver?.address?.streetAddress,
          lat: selectedDriver?.address?.coordinates[1],
          lng: selectedDriver?.address?.coordinates[0],
        });
        setAddBooking({
          ...addBooking,
          endLocation: {
            streetAddress: selectedDriver?.address?.streetAddress,
            coordinates: selectedDriver?.address?.coordinates,
            type: selectedDriver?.address?.type,
          },
          driver: { driverId: selectedDriver._id, driverName: selectedDriver.driverName },
        });
      }
      updateTripName(addBooking.company, addBooking.tripSubType, selectedDriver);
    }
  };

  const handleChangeForVehicle = (e: any) => {
    const { value } = e.target;
    if (!value) {
      setAddBooking({ ...addBooking, vehicle: { vehicleId: "", vehicleName: "" } });
      return;
    }
    const selectedVehicle: any = getAllVehicle.find((vehicle: any) => vehicle._id === value);
    if (selectedVehicle) {
      setAddBooking({ ...addBooking, vehicle: { vehicleId: selectedVehicle._id, vehicleName: selectedVehicle.vehicleName } });
    }
  };

  useEffect(() => {
    if (startLocation?.lng && startLocation?.lat && addBooking?.startLocation.coordinates.length === 0) {
      setAddBooking({
        ...addBooking,
        startLocation: { streetAddress: startLocation?.address, coordinates: [startLocation?.lat, startLocation?.lng], type: "Point" },
      });
    }
  }, [addBooking]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      const response = await dispatch(fetchDriver(1, 999999999, searchDriver));
      if (response && response?.data?.data?.length && response?.isSuccess) {
        setAllDriver(response?.data?.data);
        setTotalCount(response?.data?.totalCount);
      } else {
        setAllDriver([]);
        setTotalCount(0);
      }
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [searchDriver, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setIsEdit(true);
        const response: any = await dispatch(fetchBookingById(id));
        const { customer, vehicle, driver, tripType, tripSubType, startLocation, company, endLocation, approxTotalKm, tripStartDate, tripEndDate, totalAmount, tripStatus, status, startTime, tripName, isEmailSend } = response?.data?.findBookingById;
        setAddBooking({
          customer: customer?.length ? customer : [{ customerId: "", customerName: "", pickupTime: 0 }],
          vehicle: { vehicleId: vehicle?.vehicleId || "", vehicleName: vehicle?.vehicleName || "" },
          driver: { driverId: driver?.driverId || "", driverName: driver?.driverName || "" },
          tripType: tripType || "Single Trip",
          tripSubType: tripSubType || "homeToOffice",
          startLocation: startLocation || {},
          company: company || { companyId: "", companyName: "" },
          endLocation: endLocation || {},
          approxTotalKm: approxTotalKm || "",
          tripStartDate: tripStartDate || 0,
          tripEndDate: tripEndDate || 0,
          totalAmount: totalAmount || 1,
          tripStatus: tripStatus || "yetToStart",
          status: status || "",
          startTime: startTimeWithTodaysDate(startTime || 0),
          tripName: tripName || "",
          isEmailSend: isEmailSend || false,
          _id: id || "",
        });
        setStartLocation({ address: startLocation.streetAddress, lat: startLocation.coordinates[1], lng: startLocation.coordinates[0] });
        setEndLocation({ address: endLocation.streetAddress, lat: endLocation.coordinates[1], lng: endLocation.coordinates[0] });
      } else {
        setIsEdit(false);
        setAddBooking({
          customer: [],
          vehicle: { vehicleId: "", vehicleName: "" },
          driver: { driverId: "", driverName: "" },
          tripType: "Single Trip",
          tripSubType: "homeToOffice",
          startLocation: { coordinates: [], type: "Point", streetAddress: "" },
          company: { companyId: "", companyName: "" },
          endLocation: { coordinates: [], type: "Point", streetAddress: "" },
          approxTotalKm: "",
          tripStartDate: 0,
          tripEndDate: 0,
          totalAmount: 1,
          tripStatus: "yetToStart",
          status: "",
          startTime: 0,
          tripName: "",
          isEmailSend: false,
          _id: "",
        });
      }
    };
    fetchData();
  }, [id, navigate, dispatch]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      const response = await dispatch(fetchVehicle(1, 10, search));
      if (response && response?.data?.data?.length && response?.isSuccess) {
        setAllVehicle(response?.data?.data);
        setTotalCount(response?.data?.totalCount);
      } else {
        setAllVehicle([]);
        setTotalCount(0);
      }
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [search, dispatch]);

  useEffect(() => {
    const getAllCustomer = async () => {
      const response: any = await dispatch(fetchCustomer(1, 99999999, "", "active", companyId));
      if (response && response?.data?.data?.length > 0 && response?.isSuccess) {
        setAllCustomer(response?.data?.data);
      } else {
        setAllCustomer([]);
      }
    };
    getAllCustomer();
  }, [dispatch, companyId]);

  const handleChange = (e: any) => {
    if (e && e.target) {
      let { name: inputName, value } = e.target;
      if (inputName.startsWith("startLocation.") || inputName.startsWith("endLocation.")) {
        const [section, field] = inputName.split(".");
        setAddBooking((prevData: any) => ({ ...prevData, [section]: { ...prevData[section], [field]: value } }));
        if (field === "streetAddress") {
          if (typingTimeout) clearTimeout(typingTimeout);
          const timeout = setTimeout(() => {
            if (value.trim().length > 2) {
              fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=pk&q=${encodeURIComponent(value)}`)
                .then((res) => res.json())
                .then((data) => setSuggestions((prev: any) => ({ ...prev, [section]: data })))
                .catch((err) => console.error(err));
            } else {
              setSuggestions((prev: any) => ({ ...prev, [section]: [] }));
            }
          }, 400);
          setTypingTimeout(timeout);
        }
      } else if (inputName === "tripSubType") {
        let selectedDriver: any = allDriver.find((driver: any) => driver._id === addBooking.driver.driverId);
        if (selectedDriver) {
          if (value === "officeToHome") {
            setAddBooking((prevData: any) => ({ ...prevData, endLocation: { streetAddress: selectedDriver?.address?.streedAddres, coordinates: selectedDriver?.address?.coordinates, type: "Point" } }));
            setEndLocation({ address: selectedDriver?.address?.streedAddres, lat: selectedDriver?.address?.coordinates[1], lng: selectedDriver?.address?.coordinates[0] });
          }
          if (value === "homeToOffice") {
            setAddBooking((prevData: any) => ({ ...prevData, startLocation: { streetAddress: selectedDriver?.address?.streedAddres, coordinates: selectedDriver?.address?.coordinates, type: "Point" } }));
            setStartLocation({ address: selectedDriver?.address?.streedAddres, lat: selectedDriver?.address?.coordinates[1], lng: selectedDriver?.address?.coordinates[0] });
          }
        }
        setAddBooking((prevData) => ({ ...prevData, [inputName]: value }));
        updateTripName(addBooking.company, value, addBooking.driver);
      } else {
        setAddBooking((prevData) => ({ ...prevData, [inputName]: inputName === "totalAmount" ? Number(value) : value }));
      }
    } else {
      setAddBooking((prevData: any) => ({ ...prevData, selectedOptions: e }));
    }
  };

  const handleCustomerSelect = (event: any) => {
    const selectedIds: string[] = event?.target?.value;
    const customerMap = new Map(allCustomer.map((c: any) => [c._id, c]));
    const prevIds = new Set(addBooking.customer.map((c: any) => c.customerId));
    const selectedSet = new Set(selectedIds);

    const updated = selectedIds.map((id: string) => {
      const existing = addBooking.customer.find((c: any) => c.customerId === id);
      if (existing) return existing;
      const found = customerMap.get(id);
      if (!found) return null;
      return {
        customerId: found._id,
        customerName: found.name,
        pickupTime: 0,
        customerPickupLocation: found.address || found.customerPickupLocation || null,
        additionalInfo: [],
        getAdditionalInfo: found.additionalInfo || [],
      };
    }).filter(Boolean);

    setAddBooking((prev: any) => ({ ...prev, customer: updated }));
  };

  const handleTimeChange = (customerId: string, newTime: any) => {
    if (!newTime) return;
    const selected: any = selectedCustomers.find((cust: any) => cust._id === customerId);
    if (!selected) return;
    const tripDate = addBooking.tripStartDate ? new Date(addBooking.tripStartDate) : new Date();
    tripDate.setHours(newTime.hour(), newTime.minute(), 0, 0);
    const pickupTime = tripDate.getTime();
    setAddBooking((prev: any) => ({
      ...prev,
      customer: prev.customer.map((c: any) => (c.customerId === customerId ? { ...c, pickupTime } : c)),
    }));
  };

  useEffect(() => {
    if (!startLocation?.lat && !startLocation?.lng) setDirections([]);
    if (!endLocation?.lat && !endLocation?.lng) setDirections([]);
  }, [startLocation, endLocation]);

  const handelTripStartTime = (newTime: any) => {
    if (!newTime) {
      setSelectedTime(null);
      setAddBooking((prev: any) => ({ ...prev, startTime: 0 }));
      return;
    }
    const startTime = isEdit
      ? dayjs().hour(newTime.hour()).minute(newTime.minute()).second(0).millisecond(0).valueOf()
      : newTime.valueOf();
    setSelectedTime(newTime);
    setAddBooking((prev: any) => ({ ...prev, startTime }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (addBooking?.endLocation?.coordinates?.length === 0 && addBooking?.tripSubType === "homeToOffice") {
        addBooking.endLocation = selectedCompanyLocation;
      }
      const { vehicle, driver, customer, approxTotalKm, endLocation, startLocation, totalAmount, tripEndDate, tripStartDate, tripStatus, tripType, tripSubType, isEmailSend, company } = addBooking;
      if (vehicle.vehicleId === "" || vehicle.vehicleName === "" || driver.driverId === "" || driver.driverName === "" || customer.length === 0 || endLocation.streetAddress === "" || startLocation.streetAddress === "" || tripStartDate === 0 || tripStatus === "" || tripSubType === "" || company.companyId === "") {
        toast.error("Fill all required fields!");
        setLoading(false);
        return;
      }
      const cleanedCustomers = customer.map((cust: any) => { const { getAdditionalInfo, ...rest } = cust; return rest; });
      const cleanedBooking = { ...addBooking, customer: cleanedCustomers };
      if (isEdit) {
        const res: any = await dispatch(editBooking(cleanedBooking));
        if (res.data && res.isSuccess) { toast.success(res.message || "Booking Updated!"); navigate("/booking-list"); }
        else { toast.error(res.message || "Booking Not Updated!"); return; }
      } else {
        const res: any = await dispatch(addNewBooking(cleanedBooking));
        if (res.data && res.isSuccess) { toast.success(res.message || "Booking Added!"); navigate("/booking-list"); }
        else { toast.error(res.message || "Booking Not Added!"); return; }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerRemove = (customerId: string) => {
    const updated = addBooking.customer.filter((cust: any) => cust.customerId !== customerId);
    setAddBooking((prev: any) => ({ ...prev, customer: updated }));
  };

  const handleChangeforChecked = (event: any) => {
    setAddBooking({ ...addBooking, [event.target.name]: event.target.checked });
  };

  const navigateTodbookingList = () => navigate("/booking-list");

  const updateTripName = (school: any, subType: string, driver: any) => {
    let name = "";
    if (school) name += school.companyName;
    if (subType) { const formatted = formatTripSubType(subType); name += ` - ${formatted}`; }
    if (driver) name += ` - ${driver.driverName}`;
    setAddBooking((prev) => ({ ...prev, tripName: name }));
  };

  const formatTripSubType = (subType: string) => {
    if (subType === "homeToOffice") return tenantConfig.mode === "company" ? "HTO" : "HTS";
    if (subType === "officeToHome") return tenantConfig.mode === "company" ? "OTH" : "STH";
    return subType;
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (startLocation && endLocation) {
      const ds = new window.google.maps.DirectionsService();
      ds.route(
        { origin: { lat: startLocation.lat, lng: startLocation.lng }, destination: { lat: endLocation.lat, lng: endLocation.lng }, travelMode: window.google.maps.TravelMode.DRIVING },
        (result: any, status: string) => {
          if (status === "OK") { const route = result.routes[0].overview_path.map((p: any) => ({ lat: p.lat(), lng: p.lng() })); setDirections(route); }
          else { console.error("Directions error:", status); }
        }
      );
    } else {
      setDirections([]);
    }
  }, [isLoaded, startLocation, endLocation]);

  if (loadError) return <div>Map load error: {String(loadError)}</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;

  function Row({ item }: RowProps) {
    const [inputValue, setInputValue] = useState(item.customerPickupLocation?.streetAddress || "");
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.customerId });
    const [isHovered, setIsHovered] = useState(false);

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      background: isDragging ? "#e0e0e0" : isHovered ? "rgb(244, 246, 249)" : "rgb(249, 249, 249)",
      cursor: "grab",
    };

    const inputRef = useRef<HTMLInputElement>(null);

    const onLoad = (auto: google.maps.places.Autocomplete) => setAutocomplete(auto);

    const onPlaceChanged = () => {
      if (autocomplete !== null) {
        const place = autocomplete.getPlace();
        const lat = place?.geometry?.location?.lat?.();
        const lng = place?.geometry?.location?.lng?.();
        if (place?.geometry && typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng)) {
          const formattedAddress = place.formatted_address || "";
          setAddBooking((prev: any) => ({
            ...prev,
            customer: prev.customer.map((c: any) =>
              c.customerId === item.customerId ? { ...c, customerPickupLocation: { streetAddress: formattedAddress, coordinates: [lng, lat], type: "Point" } } : c
            ),
          }));
          setInputValue(formattedAddress);
        }
      }
    };

    const handleChildSelect = (customerId: string, newValue: any[]) => {
      setAddBooking((prev: any) => ({
        ...prev,
        customer: prev.customer.map((c: any) => (c.customerId === customerId ? { ...c, additionalInfo: newValue } : c)),
      }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      if (value === "") {
        setAddBooking((prev: any) => ({
          ...prev,
          customer: prev.customer.map((c: any) => (c.customerId === item.customerId ? { ...c, customerPickupLocation: null } : c)),
        }));
      }
    };

    return (
      <>
        <tr ref={setNodeRef} style={style} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <td>
            <div className="d-flex align-items-center">
              <span {...listeners} style={{ cursor: "grab", display: "inline-block" }}><RiDragMove2Fill /></span>
              <span className="cusName" style={{ marginLeft: "10px" }}>{item.customerName}</span>
            </div>
          </td>
          <td>
            <TimePicker
              value={item.pickupTime ? dayjs(item.pickupTime) : null}
              onChange={(newTime) => handleTimeChange(item.customerId, newTime)}
              slotProps={{ textField: { size: "small", sx: { fontSize: 10, width: 140 } } }}
            />
          </td>
          <td>
            <GMapAutocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged} options={{ componentRestrictions: { country: "pk" }, types: ["geocode", "establishment"], fields: ["address_components", "geometry", "icon", "name", "formatted_address"] }}>
              <input ref={inputRef} type="text" placeholder="Search Pakistan Locations" value={inputValue} onChange={handleInputChange} style={{ width: "100%", padding: "10px", border: "1px solid #ccc", borderRadius: "8px" }} onMouseDown={(e) => e.stopPropagation()} />
            </GMapAutocomplete>
          </td>
          <td>
            <IconButton color="error" onClick={(e) => { e.stopPropagation(); handleCustomerRemove(item.customerId); }}>
              <DeleteIcon style={{ height: "30px" }} />
            </IconButton>
          </td>
        </tr>
        {canAddChild && (
          <tr ref={setNodeRef} style={style} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <td colSpan={4}>
              <Autocomplete
                multiple
                open={openAutoId === item.customerId}
                onOpen={() => setOpenAutoId(item.customerId)}
                onClose={() => setOpenAutoId(null)}
                disableCloseOnSelect
                clearOnBlur={false}
                options={item.getAdditionalInfo?.length > 0 ? item.getAdditionalInfo : [{ childName: "No Child Added" }]}
                getOptionLabel={(option) => option.childName || ""}
                value={item.additionalInfo || []}
                isOptionEqualToValue={(option, value) => option.childName === value.childName}
                onChange={(event, newValue) => {
                  if (newValue.some((opt: any) => opt.childName === "No Child Added")) return;
                  handleChildSelect(item.customerId, newValue);
                  setOpenAutoId(item.customerId);
                }}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Child" sx={{ "& .MuiInputBase-root": { padding: "3px 9px" } }} />}
              />
            </td>
          </tr>
        )}
      </>
    );
  }

  const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: "10px", backgroundColor: "#E3F2FD", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e" }}>{title}</Typography>
    </Box>
  );

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "transparent",
      position: "relative",
      overflow: "hidden",
      "&::before": { content: '""', position: "absolute", top: -50, right: -50, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,109,255,0.05) 0%, transparent 70%)", pointerEvents: "none" },
    }}>
      <ToastContainer />
      {/* Header - Outside Card */}
      <Box sx={{ maxWidth: 1900, mx: "auto", mt: 1, mb: 2, px: 0, display: "flex", alignItems: "center", gap: 1.5, position: "relative", zIndex: 1 }}>
        <IconButton onClick={navigateTodbookingList} sx={{ color: "#555", "&:hover": { backgroundColor: "#f5f5f5" } }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e90ff" }}>
          {isEdit ? "Edit Trip" : "Add New Trip"}
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 1900, mx: "auto", mb: 4, borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid #eef0f5", position: "relative", zIndex: 1 }}>
        <CardContent sx={{ p: 4 }}>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Trip Information */}
            <Box sx={{ mb: 3 }}>
              <SectionHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>} title="Trip Information" />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr 1fr" }, gap: 2 }}>
                {renderField("Trip Start Date", true,
                  <TextField fullWidth type="date" name="tripStartDate" value={addBooking.tripStartDate ? new Date(addBooking.tripStartDate).toISOString().split("T")[0] : ""} onChange={(e) => setAddBooking({ ...addBooking, tripStartDate: new Date(e.target.value).getTime() })} sx={textFieldSx} slotProps={{ inputLabel: { shrink: true } }} />
                )}
                {renderField("Trip End Date", true,
                  <TextField fullWidth type="date" name="tripEndDate" value={addBooking.tripEndDate ? new Date(addBooking.tripEndDate).toISOString().split("T")[0] : ""} onChange={(e) => setAddBooking({ ...addBooking, tripEndDate: new Date(e.target.value).getTime() })} sx={textFieldSx} slotProps={{ inputLabel: { shrink: true } }} />
                )}
                {renderField("Trip Start Time", true,
                  <TimePicker value={addBooking.startTime ? dayjs(addBooking.startTime) : null} timeSteps={{ minutes: 1 }} onChange={(newValue) => handelTripStartTime(newValue)} slotProps={{ textField: { fullWidth: true, placeholder: "Select Time", sx: textFieldSx } }} />
                )}
                {renderField("Trip Type", true,
                  <TextField select fullWidth name="tripSubType" value={addBooking.tripSubType} onChange={handleChange} sx={selectSx}>
                    <MenuItem value="homeToOffice">{tenantConfig.mode === "company" ? "Home to Office" : `Home to ${schoolLabel}`}</MenuItem>
                    <MenuItem value="officeToHome">{tenantConfig.mode === "company" ? "Office to Home" : `${schoolLabel} to Home`}</MenuItem>
                  </TextField>
                )}
                {renderField("Trip Status", true,
                  <TextField select fullWidth name="tripStatus" value={addBooking.tripStatus} onChange={handleChange} sx={selectSx}>
                    <MenuItem value="yetToStart">Yet to Start</MenuItem>
                    <MenuItem value="completed" disabled={addBooking.status === "rideStarted"}>Completed</MenuItem>
                    <MenuItem value="onGoing">OnGoing</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </TextField>
                )}
              </Box>
            </Box>

            {/* Location Details */}
            <Box sx={{ mb: 3 }}>
              <SectionHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>} title="Location Details" />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr" }, gap: 3 }}>
                <Box sx={{ position: "relative" }}>
                  {renderField("Address / Start Point", true,
                    <TextField
                      fullWidth
                      placeholder="e.g. Street # 23, Main mal road, Karachi"
                      value={startLocation?.address || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDirections([]);
                        if (value === "") {
                          setStartLocation(null);
                          setSuggestions((prev: any) => ({ ...prev, startLocation: [] }));
                        } else {
                          setStartLocation((prev: any) => ({ ...prev, address: value }));
                          if (typingTimeout) clearTimeout(typingTimeout);
                          const timeout = setTimeout(() => {
                            if (value.trim().length > 2) {
                              fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=pk&q=${encodeURIComponent(value)}`)
                                .then((res) => res.json())
                                .then((data) => setSuggestions((prev: any) => ({ ...prev, startLocation: data })))
                                .catch((err) => console.error(err));
                            } else {
                              setSuggestions((prev: any) => ({ ...prev, startLocation: [] }));
                            }
                          }, 400);
                          setTypingTimeout(timeout);
                        }
                      }}
                      sx={textFieldSx}
                    />
                  )}
                  {suggestions.startLocation.length > 0 && (
                    <Box sx={{ position: "absolute", zIndex: 10, bgcolor: "#fff", border: "1px solid #d0d5dd", borderRadius: "8px", mt: 0.5, maxHeight: 200, overflow: "auto", width: "100%", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                      {suggestions.startLocation.map((s: any, i: number) => (
                        <Box
                          key={i}
                          sx={{ px: 1.5, py: 1, cursor: "pointer", fontSize: "0.85rem", "&:hover": { backgroundColor: "#f0eeff" } }}
                          onClick={() => {
                            setStartLocation({ address: s.display_name, lat: parseFloat(s.lat), lng: parseFloat(s.lon) });
                            setAddBooking((prev: any) => ({
                              ...prev,
                              startLocation: { streetAddress: s.display_name, coordinates: [parseFloat(s.lon), parseFloat(s.lat)], type: "Point" },
                            }));
                            setSuggestions((prev: any) => ({ ...prev, startLocation: [] }));
                          }}
                        >
                          {s.display_name}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Box sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={startLocation ? { lat: startLocation?.lat, lng: startLocation?.lng } : defaultCenter}
                    zoom={startLocation ? 12 : 10}
                    onLoad={(m: any) => { setMap(m); mapRef.current = m; }}
                  >
                    {startLocation && (
                      <Marker position={{ lat: startLocation?.lat, lng: startLocation?.lng }} title="Start Location" icon={{ url: "/startLocation.png", scaledSize: new window.google.maps.Size(40, 40) }} />
                    )}
                    {endLocation && (
                      <Marker position={{ lat: endLocation?.lat, lng: endLocation?.lng }} title="End Location" icon={{ url: "/endLocation.png", scaledSize: new window.google.maps.Size(40, 40) }} />
                    )}
                    {addBooking?.customer?.map((cust: any, index: number) => {
                      const loc = cust?.customerPickupLocation;
                      if (!loc) return null;
                      const lat = Number(loc?.lat ?? loc?.coordinates?.[1]);
                      const lng = Number(loc?.lng ?? loc?.coordinates?.[0]);
                      if (isNaN(lat) || isNaN(lng)) return null;
                      return <Marker key={index} position={{ lat, lng }} title={cust?.customerName || `Customer ${index + 1}`} icon={{ url: "/parent.png", scaledSize: new window.google.maps.Size(40, 40) }} />;
                    })}
                    {Array.isArray(directions) && directions.length > 1 && (
                      <Polyline path={directions.map((p: any) => ({ lat: Number(p.lat), lng: Number(p.lng) }))} options={{ strokeColor: "#1a73e8", strokeOpacity: 0.8, strokeWeight: 5 }} />
                    )}
                  </GoogleMap>
                </Box>
              </Box>
            </Box>

            {/* Company & Employee */}
            <Box sx={{ mb: 3 }}>
              <SectionHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>} title={schoolLabel} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                <Box>
                  {renderField(schoolLabel, true,
                    <Autocomplete
                      options={allCompany}
                      getOptionLabel={(option: any) => option.companyName}
                      value={allCompany.find((v: any) => v._id === addBooking.company?.companyId) || null}
                      onInputChange={(event, newInputValue) => setSearchCompany(newInputValue)}
                      onChange={(event, newValue: any) => {
                        if (newValue) handleChangeForCompany({ target: { value: newValue._id } });
                        else {
                          handleChangeForCompany({ target: { value: "" } });
                          if (addBooking.tripSubType === "homeToOffice") {
                            setAddBooking((prev: any) => ({ ...prev, company: {}, endLocation: { coordinates: [], streetAddress: "", type: "Point" } }));
                            setEndLocation({ address: "", lat: 0, lng: 0 });
                          }
                          if (addBooking.tripSubType === "officeToHome") {
                            setAddBooking((prev: any) => ({ ...prev, company: {}, startLocation: { coordinates: [], streetAddress: "", type: "Point" } }));
                            setStartLocation({ address: "", lat: 0, lng: 0 });
                          }
                        }
                      }}
                      clearOnEscape
                      ListboxProps={{ sx: { backgroundColor: "#fff", color: "#000", "& .MuiAutocomplete-option": { color: "#000" } } }}
                      renderInput={(params) => <TextField {...params} placeholder={`Select or Search ${schoolLabel}`} sx={textFieldSx} />}
                    />
                  )}
                </Box>
                <Box>
                  {renderField("Employee Name", true,
                    <Autocomplete
                      multiple
                      options={allCustomer}
                      disableCloseOnSelect
                      getOptionLabel={(option: any) => option.name || ""}
                      value={allCustomer.filter((cust: any) => addBooking.customer.some((c: any) => c.customerId === cust._id))}
                      onChange={(event, newValue: any[]) => {
                        const isSelectAll = newValue.some((v: any) => v._id === "select-all");
                        if (isSelectAll) {
                          const allIds = allCustomer.map((c: any) => c._id);
                          const customerMap = new Map(allCustomer.map((c: any) => [c._id, c]));
                          const updated = allIds.map((id: string) => {
                            const found = customerMap.get(id);
                            if (!found) return null;
                            return {
                              customerId: found._id,
                              customerName: found.name,
                              pickupTime: 0,
                              customerPickupLocation: found.address || found.customerPickupLocation || null,
                              additionalInfo: [],
                              getAdditionalInfo: found.additionalInfo || [],
                            };
                          }).filter(Boolean);
                          setAddBooking((prev: any) => ({ ...prev, customer: updated }));
                        } else {
                          handleCustomerSelect({ target: { value: newValue.map((c: any) => c._id) } });
                        }
                      }}
                      renderOption={(props, option, { selected }) => {
                        if (option._id === "select-all") {
                          const allSelected = addBooking.customer.length === allCustomer.length && allCustomer.length > 0;
                          return (
                            <li {...props} style={{ borderBottom: "1px solid #e0e0e0" }}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={allSelected}
                              />
                              <span style={{ fontWeight: 700, color: "#7c6dff" }}>Select All</span>
                            </li>
                          );
                        }
                        return (
                          <li {...props}>
                            <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                            {option.name}
                          </li>
                        );
                      }}
                      filterOptions={(options, state) => {
                        const filtered = options.filter((o: any) => o._id !== "select-all");
                        const displayOptions = [{ _id: "select-all", name: "Select All" }, ...filtered];
                        return displayOptions.filter((opt: any) => {
                          if (opt._id === "select-all") return true;
                          return opt.name?.toLowerCase().includes(state.inputValue.toLowerCase());
                        });
                      }}
                      renderInput={(params) => <TextField {...params} placeholder={`Select or Search ${parentLabelPlural}`} sx={textFieldSx} />}
                      renderTags={() => null}
                      ListboxProps={{ sx: { backgroundColor: "#fff", color: "#000", "& .MuiAutocomplete-option": { color: "#000" } } }}
                    />
                  )}
                </Box>
              </Box>

              {/* Customer Table */}
              {addBooking.customer.length > 0 && (
                <Box sx={{ mt: 2, overflow: "auto", maxHeight: "340px", borderRadius: "12px", border: "1px solid #e0e0e0" }}>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={addBooking.customer} strategy={verticalListSortingStrategy}>
                      <table cellPadding={10} style={{ borderCollapse: "collapse", width: "100%" }}>
                        <thead>
                          <tr>
                            <th style={{ padding: "10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, fontSize: "0.8rem", color: "#555" }}>{parentLabel} Name</th>
                            <th style={{ padding: "10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, fontSize: "0.8rem", color: "#555" }}>Pickup Time</th>
                            <th style={{ padding: "10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, fontSize: "0.8rem", color: "#555" }}>Pickup Location <span style={{ color: "red" }}>*</span></th>
                            <th style={{ padding: "10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, fontSize: "0.8rem", color: "#555" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {addBooking.customer.map((cust: any) => <Row key={cust.customerId} item={cust} />)}
                        </tbody>
                      </table>
                    </SortableContext>
                  </DndContext>
                </Box>
              )}
            </Box>

            {/* Vehicle, Driver, Trip Plan */}
            <Box sx={{ mb: 3 }}>
              <SectionHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>} title="Vehicle & Driver" />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
                <Box>
                  {renderField("Vehicle", true,
                    <Autocomplete
                      options={getAllVehicle}
                      getOptionLabel={(option: any) => option.vehicleName}
                      value={getAllVehicle.find((v: any) => v._id === addBooking.vehicle?.vehicleId) || null}
                      onInputChange={(event, newInputValue) => setSearch(newInputValue)}
                      onChange={(event, newValue: any) => handleChangeForVehicle({ target: { value: newValue ? newValue._id : "" } })}
                      clearOnEscape
                      ListboxProps={{ sx: { backgroundColor: "#fff", color: "#000", "& .MuiAutocomplete-option": { color: "#000" } } }}
                      renderInput={(params) => <TextField {...params} placeholder="Select or Search Vehicle" sx={textFieldSx} />}
                    />
                  )}
                </Box>
                <Box>
                  {renderField("Driver", true,
                    <Autocomplete
                      options={allDriver}
                      getOptionLabel={(option: any) => option.driverName}
                      value={allDriver.find((v: any) => v._id === addBooking.driver?.driverId) || null}
                      onInputChange={(event, newInputValue) => setSearchDriver(newInputValue)}
                      onChange={(event, newValue: any) => handleChangeForDriver({ target: { value: newValue ? newValue._id : "" } })}
                      clearOnEscape
                      ListboxProps={{ sx: { backgroundColor: "#fff", color: "#000", "& .MuiAutocomplete-option": { color: "#000" } } }}
                      renderInput={(params) => <TextField {...params} placeholder="Select or Search Driver" sx={textFieldSx} />}
                    />
                  )}
                </Box>
                <Box>
                  {renderField("Trip Plan", false,
                    <TextField fullWidth placeholder="Trip Plan" name="tripName" value={addBooking.tripName} InputProps={{ readOnly: true }} sx={textFieldSx} />
                  )}
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>

          {/* Email Checkbox + Submit */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, pt: 3, borderTop: "1px solid #eef0f5" }}>
            <FormControlLabel
              control={<MuiCheckbox checked={addBooking.isEmailSend} name="isEmailSend" onChange={handleChangeforChecked} sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }} />}
              label={<Typography variant="body2" sx={{ fontWeight: 500, color: "#555", fontSize: "0.85rem" }}>I want to send trip confirmation email to customer(s)</Typography>}
            />
            <Button
              variant="contained"
              endIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>}
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.2,
                backgroundColor: "#2563EB",
                "&:hover": { backgroundColor: "#1976d2" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : isEdit ? "Edit Trip" : "Add Trip"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddBooking;
